import { useState, useEffect, useRef } from 'react';
import { DateTime } from 'luxon';
import {
  combinePickerDateTimeGetUTC,
  pickerDateToDayKey,
} from '../helpers/combinePickerDateTimeGetUTC';
import type { CarOperatingHoursRow } from '../helpers/operatingHours';
import { outOfOperatingHoursMinutesFor } from '../helpers/operatingHours';
import { convert24To12Hour, convert12To24Hour } from '../helpers/timezoneHelpers';
import {
  DAY_MINUTES,
  MINUTE_STEP,
  disabledTimesFor,
  labelToMinutes,
  minutesToLabel12h,
  operatingHoursDisabledTimesFor,
  pickupLeadDisabledTimes,
  type BookedSlot,
} from '../helpers/bookedSlots';

// Stable empty arrays to prevent infinite re-renders from default parameter values
const EMPTY_BOOKED_DAYS: Array<{ from: Date; to: Date }> = [];
const EMPTY_UNAVAILABLE_DAYS: Array<{ from: Date; to: Date }> = [];
const EMPTY_SLOTS: BookedSlot[] = [];

/** Flutter car-details default: pickup in 3 days, return in 6, both at 10:00 AM. */
const DEFAULT_START_OFFSET_DAYS = 3;
const DEFAULT_END_OFFSET_DAYS = 6;
const DEFAULT_TIME = '10:00 AM';

interface UseBookingDateTimeProps {
  timezone?: string | null;
  /** Legacy instant ranges; prefer `bookedSlots` + `bufferHours`. */
  bookedDays?: Array<{ from: Date; to: Date }>;
  unavailableDays?: Array<{ from: Date; to: Date }>;
  /** Booked instants from `getCarBookedDates`; chips inside them (± buffer) are removed. */
  bookedSlots?: BookedSlot[];
  /** `tripBufferHours`: hours kept free before and after every booking. */
  bufferHours?: number;
  initialStartDate?: Date;
  initialEndDate?: Date;
  initialStartTime?: string;
  initialEndTime?: string;
  minimumRentalDays?: number;
  /** HOME operating hours for the listing location (matches Flutter trip picker). */
  operatingHours?: CarOperatingHoursRow | null;
}

interface BookingDateTime {
  startDate: Date;
  endDate: Date;
  startTime: string;
  endTime: string;
  startTimestamp: number | null;
  endTimestamp: number | null;
  startAtOptions: string[];
  endAtOptions: string[];
  validationError: string | null;
  setStartDate: (date: Date) => void;
  setEndDate: (date: Date) => void;
  setStartTime: (time: string) => void;
  setEndTime: (time: string) => void;
}

export const useBookingDateTime = ({
  timezone: inputTimezone,
  bookedDays = EMPTY_BOOKED_DAYS,
  unavailableDays = EMPTY_UNAVAILABLE_DAYS,
  bookedSlots = EMPTY_SLOTS,
  bufferHours = 0,
  initialStartDate,
  initialEndDate,
  initialStartTime = DEFAULT_TIME,
  initialEndTime = DEFAULT_TIME,
  minimumRentalDays = 1,
  operatingHours = null,
}: UseBookingDateTimeProps): BookingDateTime => {
  // Use inputTimezone directly - if undefined/null, we'll handle it in getAvailableTimeOptions
  // by not filtering by current time (showing all 24hrs)
  const timezone = inputTimezone ?? 'America/Los_Angeles';

  // Calculate default times
  const to12h = (time: string) =>
    time.includes('AM') || time.includes('PM') ? time : convert24To12Hour(time);
  const getDefaultStartTime = () => to12h(initialStartTime);
  const getDefaultEndTime = () => to12h(initialEndTime);

  const localCalendarDate = (daysFromToday: number) => {
    const date = new Date();
    date.setDate(date.getDate() + daysFromToday);
    date.setHours(0, 0, 0, 0);
    return date;
  };

  const [startDate, setStartDate] = useState<Date>(() => {
    if (initialStartDate) return initialStartDate;
    return localCalendarDate(DEFAULT_START_OFFSET_DAYS);
  });

  const [endDate, setEndDate] = useState<Date>(() => {
    if (initialEndDate) return initialEndDate;
    if (initialStartDate) {
      const defaultEndDate = new Date(initialStartDate);
      defaultEndDate.setDate(defaultEndDate.getDate() + minimumRentalDays);
      return defaultEndDate;
    }
    return localCalendarDate(DEFAULT_END_OFFSET_DAYS);
  });
  const [startTime, setStartTime] = useState<string>(getDefaultStartTime());
  const [endTime, setEndTime] = useState<string>(getDefaultEndTime());
  const [startTimestamp, setStartTimestamp] = useState<number | null>(null);
  const [endTimestamp, setEndTimestamp] = useState<number | null>(null);
  const [startAtOptions, setStartAtOptions] = useState<string[]>([]);
  const [endAtOptions, setEndAtOptions] = useState<string[]>([]);
  const [validationError, setValidationError] = useState<string | null>(null);

  // Track previous options to detect actual changes (prevents infinite loops)
  const prevStartOptionsRef = useRef<string>('');
  const prevEndOptionsRef = useRef<string>('');

  // Auto-adjust times when options change and current selection becomes invalid
  useEffect(() => {
    const optionsKey = startAtOptions.join(',');
    // Only run if options actually changed
    if (optionsKey !== prevStartOptionsRef.current) {
      prevStartOptionsRef.current = optionsKey;

      if (startAtOptions.length > 0 && !startAtOptions.includes(startTime)) {
        console.log('[useBookingDateTime] startTime invalid, auto-adjusting:', {
          currentStartTime: startTime,
          firstAvailable: startAtOptions[0],
        });
        setStartTime(startAtOptions[0]);
      }
    }
  }, [startAtOptions, startTime]);

  useEffect(() => {
    const optionsKey = endAtOptions.join(',');
    // Only run if options actually changed
    if (optionsKey !== prevEndOptionsRef.current) {
      prevEndOptionsRef.current = optionsKey;

      if (endAtOptions.length > 0 && !endAtOptions.includes(endTime)) {
        console.log('[useBookingDateTime] endTime invalid, auto-adjusting:', {
          currentEndTime: endTime,
          firstAvailable: endAtOptions[0],
        });
        setEndTime(endAtOptions[0]);
      }
    }
  }, [endAtOptions, endTime]);

  // Validate ordering and operating hours (parity with Flutter `validateRentalDateRange`)
  useEffect(() => {
    const startDateTime = combinePickerDateTimeGetUTC(startDate, startTime, timezone);
    const endDateTime = combinePickerDateTimeGetUTC(endDate, endTime, timezone);

    if (startDateTime && endDateTime && endDateTime <= startDateTime) {
      setValidationError('Return date and time must be after pickup date and time');
      return;
    }

    if (operatingHours && !operatingHours.alwaysAvailable) {
      const startZone = DateTime.fromISO(pickerDateToDayKey(startDate, timezone), {
        zone: timezone,
      });
      const endZone = DateTime.fromISO(pickerDateToDayKey(endDate, timezone), { zone: timezone });
      const startT24 =
        startTime.includes('AM') || startTime.includes('PM')
          ? convert12To24Hour(startTime)
          : startTime;
      const endT24 =
        endTime.includes('AM') || endTime.includes('PM') ? convert12To24Hour(endTime) : endTime;
      const [sh, sm] = startT24.split(':').map(Number);
      const [eh, em] = endT24.split(':').map(Number);
      const startMin = sh * 60 + sm;
      const endMin = eh * 60 + em;
      if (outOfOperatingHoursMinutesFor(startZone, operatingHours, 30).has(startMin)) {
        setValidationError('Pickup time is outside operating hours');
        return;
      }
      if (outOfOperatingHoursMinutesFor(endZone, operatingHours, 30).has(endMin)) {
        setValidationError('Return time is outside operating hours');
        return;
      }
    }

    setValidationError(null);
  }, [startDate, endDate, startTime, endTime, timezone, operatingHours]);

  // Update timestamps when dates or times change
  useEffect(() => {
    setStartTimestamp(combinePickerDateTimeGetUTC(startDate, startTime, timezone));
    setEndTimestamp(combinePickerDateTimeGetUTC(endDate, endTime, timezone));
  }, [startDate, endDate, startTime, endTime, timezone]);

  // Update time options when start date changes, timezone changes, or when booked/unavailable days update
  // Compare arrays to avoid unnecessary state updates that cause infinite loops
  // Use inputTimezone in dependency array to ensure effect runs when location changes, even if computed timezone is the same
  useEffect(() => {
    const newOptions = getAvailableTimeOptions(startDate, true);
    setStartAtOptions(prev => {
      // Only update if options actually changed
      if (prev.length !== newOptions.length || prev.some((opt, i) => opt !== newOptions[i])) {
        return newOptions;
      }
      return prev;
    });
  }, [
    startDate,
    inputTimezone,
    bookedDays,
    unavailableDays,
    bookedSlots,
    bufferHours,
    operatingHours,
  ]);

  // Update end time options when end date changes, timezone changes
  // Use inputTimezone in dependency array to ensure effect runs when location changes, even if computed timezone is the same
  useEffect(() => {
    const newEndAtOptions = getAvailableTimeOptions(endDate, false);

    // Only update if options actually changed
    setEndAtOptions(prev => {
      if (
        prev.length !== newEndAtOptions.length ||
        prev.some((opt, i) => opt !== newEndAtOptions[i])
      ) {
        // Auto-adjust end time if it's no longer valid (same day scenario)
        const startInZone = DateTime.fromJSDate(startDate).setZone(timezone);
        const endInZone = DateTime.fromJSDate(endDate).setZone(timezone);
        const isSameDay =
          startInZone.year === endInZone.year &&
          startInZone.month === endInZone.month &&
          startInZone.day === endInZone.day;

        if (isSameDay && newEndAtOptions.length > 0) {
          // Check if current endTime is in the valid options
          const isEndTimeValid = newEndAtOptions.includes(endTime);

          // Also check if endTime equals startTime (must be strictly after)
          const isEndTimeSameAsStart = endTime === startTime;

          if (!isEndTimeValid || isEndTimeSameAsStart) {
            // Auto-select the first valid option (which is right after start time)
            setEndTime(newEndAtOptions[0]);
          }
        }
        return newEndAtOptions;
      }
      return prev;
    });
  }, [
    endDate,
    startDate,
    startTime,
    inputTimezone,
    bookedDays,
    unavailableDays,
    bookedSlots,
    bufferHours,
    operatingHours,
  ]);

  /**
   * 30-minute chips for one picker, 12-hour labels. Rules mirror the Flutter
   * sheet: booked slots widened by the buffer, operating hours, a 90-minute lead
   * for a pickup on the listing's today, and a return strictly after the pickup.
   */
  const getAvailableTimeOptions = (selectedDate: Date, isStartDate: boolean) => {
    const hasTimezone = inputTimezone !== null && inputTimezone !== undefined;
    const dayKey = pickerDateToDayKey(selectedDate, timezone);

    const disabled = new Set<number>();
    disabledTimesFor({ dayKey, slots: bookedSlots, bufferHours, timezone }).forEach(m =>
      disabled.add(m),
    );
    operatingHoursDisabledTimesFor({ dayKey, timezone, operatingHours }).forEach(m =>
      disabled.add(m),
    );
    if (hasTimezone) {
      // Pickup on the listing's today needs the lead; the return only has to follow the pickup.
      pickupLeadDisabledTimes({ dayKey, timezone, minuteStep: MINUTE_STEP }).forEach(m =>
        disabled.add(m),
      );
    }

    // Legacy instant ranges (callers that have no slot list).
    const legacyRanges = [...bookedDays, ...unavailableDays];

    const startDayKey = pickerDateToDayKey(startDate, timezone);
    const startMinute = labelToMinutes(startTime);
    const sameDayAsStart = !isStartDate && dayKey === startDayKey;
    const startInstant = combinePickerDateTimeGetUTC(startDate, startTime, timezone);

    const options: string[] = [];
    for (let minute = 0; minute < DAY_MINUTES; minute += MINUTE_STEP) {
      if (disabled.has(minute)) continue;
      const instant = combinePickerDateTimeGetUTC(
        selectedDate,
        minutesToLabel12h(minute),
        timezone,
      );

      if (!isStartDate) {
        // Same day: at least one step after the pickup. Other days: after the pickup instant.
        if (sameDayAsStart && !Number.isNaN(startMinute) && minute <= startMinute) continue;
        if (!sameDayAsStart && instant <= startInstant) continue;
      }

      if (legacyRanges.length > 0) {
        const clash = legacyRanges.some(range => {
          const from = new Date(range.from).getTime();
          const to = new Date(range.to).getTime();
          const fromDay = DateTime.fromMillis(from).setZone(timezone).toISODate();
          const toDay = DateTime.fromMillis(to).setZone(timezone).toISODate();
          if (dayKey === fromDay) return instant >= from;
          if (dayKey === toDay) return instant <= to;
          return instant >= from && instant <= to;
        });
        if (clash) continue;
      }

      options.push(minutesToLabel12h(minute));
    }
    return options;
  };

  return {
    startDate,
    endDate,
    startTime,
    endTime,
    startTimestamp,
    endTimestamp,
    startAtOptions,
    endAtOptions,
    validationError,
    setStartDate,
    setEndDate,
    setStartTime,
    setEndTime,
  };
};
